import { useState, useRef, useCallback, memo, useEffect } from "react";

function useIsMobile() {
  const [m, setM] = useState(typeof window !== "undefined" && window.innerWidth <= 768);
  useEffect(() => {
    const f = () => setM(window.innerWidth <= 768);
    window.addEventListener("resize", f);
    return () => window.removeEventListener("resize", f);
  }, []);
  return m;
}

const C = {
  page:"#f8fafc",white:"#fff",blue:"#2563eb",blueDim:"#eff6ff",blueBdr:"#bfdbfe",
  blueD:"#1d4ed8",green:"#16a34a",greenDim:"#f0fdf4",greenBdr:"#bbf7d0",
  amber:"#d97706",amberDim:"#fffbeb",amberBdr:"#fde68a",
  text:"#111827",text2:"#374151",muted:"#6b7280",light:"#9ca3af",
  border:"#e5e7eb",bg:"#f9fafb",
};

const API_URL = `${process.env.REACT_APP_API_URL || "https://api.mioprice.com"}/api/healthcare`;
const PAYERS = [{value:"UHC",label:"UHC (United Healthcare)"},{value:"Aetna",label:"Aetna"},{value:"Cigna",label:"Cigna"}];
const PLAN_TYPES = ["PPO","HMO","HSA","EPO","HDHP"];
const STEPS = ["Setup","Compare Plans","Recommendation"];

const CONDITION_PROCEDURES = {
  diabetes:     ["blood glucose test","hba1c blood test"],
  hypertension: ["ekg electrocardiogram","blood pressure"],
  asthma:       ["pulmonary function test","chest x-ray"],
  copd:         ["pulmonary function test","bronchodilator therapy"],
  pregnancy:    ["obstetric ultrasound","prenatal visit"],
  mentalhealth: ["therapy session","psychiatric evaluation"],
  heartdisease: ["echocardiogram","stress test cardiac"],
  stroke:       ["brain mri","neurology visit"],
  cancer:       ["mammogram screening","oncology visit"],
  kidney:       ["kidney function test","nephrology visit"],
  orthopedic:   ["joint x-ray","physical therapy"],
  gastrointestinal: ["colonoscopy","gastroenterology visit"],
  none:         ["annual physical exam"],
};
const CONDITION_LABELS = {
  diabetes:         "Diabetes",
  hypertension:     "Hypertension",
  heartdisease:     "Heart Disease",
  stroke:           "Stroke",
  asthma:           "Asthma",
  copd:             "COPD / Emphysema / Chronic Bronchitis",
  cancer:           "Cancer / Oncology",
  mentalhealth:     "Mental Health",
  pregnancy:        "Pregnancy / Maternity",
  kidney:           "Kidney Disease",
  orthopedic:       "Orthopedic / Joint Issues",
  gastrointestinal: "Gastrointestinal",
  none:             "No chronic conditions",
};

const fmt = (n) => n==null?"—":"$"+Math.round(n).toLocaleString();

// Returns out-of-pocket cost breakdown for a given visit count
function calcOOP(plan, avgRate, visits, memberCount) {
  const annualProcCost = avgRate * visits * memberCount;
  const annualPremium  = (plan.monthlyPremium||0) * 12 * memberCount;
  const hsaContrib     = plan.type==="HSA" ? (plan.hsaContribution||0)*memberCount : 0;
  const ded  = (plan.deductible||0) * memberCount;
  const oop  = (plan.oopMax||0) * memberCount;
  const coins = (plan.coinsurance||20) / 100;
  const copayTotal = (plan.copay||0) * visits * memberCount;
  // Medical OOP: deductible + coinsurance after deductible + copays, capped at OOP max
  let medOOP = annualProcCost <= ded
    ? annualProcCost
    : ded + (annualProcCost - ded) * coins;
  medOOP = Math.min(medOOP + copayTotal, oop);
  const totalOOP = annualPremium + medOOP - hsaContrib;
  return {
    annualPremium: Math.round(annualPremium),
    medOOP:        Math.round(medOOP),          // medical out-of-pocket
    hsaContrib:    Math.round(hsaContrib),
    totalOOP:      Math.round(totalOOP),         // total out-of-pocket
    avgRate:       Math.round(avgRate),
    visits,
  };
}

// Build low / likely / high scenarios
function calcScenarios(plan, avgRate, pcpVisits, specVisits, memberCount) {
  const likelyVisits = Math.max(pcpVisits + specVisits, 1);
  const lowVisits    = Math.max(Math.floor(likelyVisits * 0.4), 1);
  const highVisits   = Math.min(likelyVisits * 2, 52); // cap at weekly
  return {
    low:    calcOOP(plan, avgRate, lowVisits,    memberCount),
    likely: calcOOP(plan, avgRate, likelyVisits, memberCount),
    high:   calcOOP(plan, avgRate, highVisits,   memberCount),
  };
}

async function callClaude(messages,system){
  const res=await fetch("https://api.anthropic.com/v1/messages",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({model:"claude-sonnet-4-6",max_tokens:1000,system,messages})});
  return(await res.json()).content?.[0]?.text||"";
}
async function fileToBase64(file){return new Promise((res,rej)=>{const r=new FileReader();r.onload=()=>res(r.result.split(",")[1]);r.onerror=rej;r.readAsDataURL(file);});}
async function fetchRates(query,zip,radius,lat,lng){const p=new URLSearchParams({query,radius,limit:500});if(lat&&lng){p.set('lat',lat);p.set('lng',lng);}else{p.set('zip',zip);}const res=await fetch(`${API_URL}?${p}`);if(!res.ok)throw new Error("API error");return(await res.json()).results||[];}

// ── Step bar ──────────────────────────────────────────────────────────────
function StepBar({current}){
  return(
    <div style={{display:"flex",padding:"12px 24px",background:C.white,borderBottom:`1px solid ${C.border}`}}>
      {STEPS.map((s,i)=>{
        const done=i<current,active=i===current;
        return(
          <div key={s} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
            <div style={{display:"flex",alignItems:"center",width:"100%"}}>
              {i>0&&<div style={{flex:1,height:2,background:done?C.blue:C.border}}/>}
              <div style={{width:28,height:28,borderRadius:"50%",flexShrink:0,background:done?C.blue:active?C.blueDim:C.bg,border:`2px solid ${done?C.blue:active?C.blue:C.border}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:700,color:done?C.white:active?C.blue:C.light}}>
                {done?"✓":i+1}
              </div>
              {i<STEPS.length-1&&<div style={{flex:1,height:2,background:done?C.blue:C.border}}/>}
            </div>
            <span style={{fontSize:11,color:active?C.blue:done?C.text2:C.light,fontWeight:active?700:400}}>{s}</span>
          </div>
        );
      })}
    </div>
  );
}

// ── PlanRow — uses local uncontrolled refs to avoid re-render lag ──────────
// Parent state is only updated on blur (when user leaves a field)
const PlanRow = memo(function PlanRow({plan, index, onBlurUpdate, onRemove}) {
  const COLS=[C.blue,C.green,C.amber,"#7C3AED","#db2777"];
  const col=COLS[index%5];

  // Local refs for each field — no re-render on every keystroke
  const nameRef   = useRef(null);
  const premRef   = useRef(null);
  const dedRef    = useRef(null);
  const oopRef    = useRef(null);
  const coinsRef  = useRef(null);
  const copayRef  = useRef(null);
  const hsaRef    = useRef(null);
  const typeRef   = useRef(plan.type||"PPO");
  const [planType, setPlanType] = useState(plan.type||"PPO");
  const mobile = useIsMobile();

  const flush = () => {
    onBlurUpdate(index, {
      type:           planType,
      name:           nameRef.current?.value  || "",
      monthlyPremium: parseFloat(premRef.current?.value)  || "",
      deductible:     parseFloat(dedRef.current?.value)   || "",
      oopMax:         parseFloat(oopRef.current?.value)   || "",
      coinsurance:    parseFloat(coinsRef.current?.value) || 20,
      copay:          parseFloat(copayRef.current?.value) || "",
      hsaContribution:parseFloat(hsaRef.current?.value)  || "",
    });
  };

  const handleTypeChange = (e) => {
    setPlanType(e.target.value);
    typeRef.current = e.target.value;
    setTimeout(flush, 0);
  };

  const inp = (ref, placeholder, defaultVal="") => (
    <input ref={ref} type="number" defaultValue={defaultVal} placeholder={placeholder}
      onBlur={flush}
      style={{width:"100%",background:"transparent",border:"none",color:C.text,fontSize:13,fontWeight:600,outline:"none",fontFamily:"inherit",padding:0}}/>
  );

  return(
    <div style={{background:C.white,border:`1.5px solid ${C.border}`,borderRadius:10,marginBottom:8,overflow:"hidden"}}>
      {/* Header row: type badge + name */}
      <div style={{display:"flex",alignItems:"stretch",borderBottom:`1px solid ${C.border}`}}>
        <div style={{display:"flex",alignItems:"center",gap:8,padding:"10px 12px",background:C.bg,borderRight:`1px solid ${C.border}`,flexShrink:0}}>
          <div style={{width:8,height:8,borderRadius:"50%",background:col}}/>
          <select value={planType} onChange={handleTypeChange}
            style={{background:"transparent",border:"none",color:col,fontSize:12,fontWeight:700,fontFamily:"inherit",cursor:"pointer",outline:"none"}}>
            {PLAN_TYPES.map(t=><option key={t}>{t}</option>)}
          </select>
        </div>
        <input ref={nameRef} defaultValue={plan.name||""} placeholder={`Plan ${index+1} name (e.g. Gold PPO, Silver HMO…)`}
          onBlur={flush}
          style={{flex:1,background:"transparent",border:"none",color:C.text,padding:"10px 12px",fontSize:13,fontWeight:500,outline:"none",fontFamily:"inherit"}}/>
        {index>0&&<button onClick={()=>onRemove(index)} style={{background:"none",border:"none",borderLeft:`1px solid ${C.border}`,color:C.light,cursor:"pointer",fontSize:18,padding:"0 14px",lineHeight:1}}>×</button>}
      </div>
      {/* Financial fields */}
      <div style={{display:"grid",gridTemplateColumns:mobile?"repeat(2,1fr)":"repeat(5,1fr)"}}>
        {[["Premium/mo ($)","premRef",premRef,plan.monthlyPremium,"450"],
          ["Deductible ($)","dedRef",dedRef,plan.deductible,"1500"],
          ["OOP Max ($)","oopRef",oopRef,plan.oopMax,"5000"],
          ["Coinsurance (%)","coinsRef",coinsRef,plan.coinsurance,"20"],
          ["Copay/visit ($)","copayRef",copayRef,plan.copay,"30"],
        ].map(([label,key,ref,val,ph],fi)=>(
          <div key={key} style={{padding:"8px 10px",borderRight:(mobile?(fi%2===0):(fi<4))?`1px solid ${C.border}`:"none",borderTop:(mobile&&fi>=2)?`1px solid ${C.border}`:"none"}}>
            <div style={{fontSize:9,color:C.light,marginBottom:3,fontWeight:500}}>{label}</div>
            <input ref={ref} type="number" defaultValue={val||""} placeholder={ph}
              onBlur={flush}
              style={{width:"100%",background:"transparent",border:"none",color:C.text,fontSize:13,fontWeight:600,outline:"none",fontFamily:"inherit",padding:0}}/>
          </div>
        ))}
      </div>
      {/* HSA contribution — only if HSA type */}
      {planType==="HSA"&&(
        <div style={{borderTop:`1px solid ${C.border}`,padding:"8px 12px",display:"flex",alignItems:"center",gap:8,background:C.greenDim}}>
          <span style={{fontSize:12,color:C.green,fontWeight:500}}>Employer HSA contribution/yr ($):</span>
          <input ref={hsaRef} type="number" defaultValue={plan.hsaContribution||""} placeholder="1000"
            onBlur={flush}
            style={{width:100,background:C.white,border:`1px solid ${C.greenBdr}`,borderRadius:6,color:C.green,fontSize:13,fontWeight:700,padding:"4px 8px",outline:"none",fontFamily:"inherit"}}/>
        </div>
      )}
    </div>
  );
});

// ── Family member row ─────────────────────────────────────────────────────
const MemberRow = memo(function MemberRow({member, index, onChange, onRemove}) {
  const ageRef = useRef(null);
  const flush  = () => onChange(index, {...member, age: ageRef.current?.value||""});

  return(
    <div style={{display:"flex",gap:8,alignItems:"center",padding:"8px 0",borderBottom:`1px solid ${C.border}`}}>
      {/* Type badge */}
      <div style={{fontSize:11,fontWeight:600,color:member.type==="adult"?C.blue:C.amber,background:member.type==="adult"?C.blueDim:C.amberDim,border:`1px solid ${member.type==="adult"?C.blueBdr:C.amberBdr}`,borderRadius:6,padding:"3px 9px",flexShrink:0,width:46,textAlign:"center"}}>
        {member.type==="adult"?"Adult":"Child"}
      </div>
      {/* Gender — adults only */}
      {member.type==="adult"?(
        <select value={member.gender||""} onChange={e=>onChange(index,{...member,gender:e.target.value})}
          style={{background:C.white,border:`1px solid ${C.border}`,borderRadius:6,color:member.gender?C.text:C.light,padding:"7px 10px",fontSize:12,fontFamily:"inherit",cursor:"pointer",outline:"none",minWidth:100}}>
          <option value="">Gender…</option>
          <option value="male">Male</option>
          <option value="female">Female</option>
          <option value="other">Other</option>
        </select>
      ):(
        <div style={{width:100,flexShrink:0}}/>
      )}
      {/* Age */}
      <div style={{display:"flex",alignItems:"center",gap:6,flex:1}}>
        <span style={{fontSize:12,color:C.muted,flexShrink:0}}>Age</span>
        <input ref={ageRef} type="number" min="0" max={member.type==="adult"?99:17}
          defaultValue={member.age||""} placeholder={member.type==="adult"?"e.g. 38":"e.g. 8"}
          onBlur={flush}
          style={{width:80,background:C.white,border:`1px solid ${C.border}`,borderRadius:6,color:C.text,padding:"7px 10px",fontSize:13,outline:"none",fontFamily:"inherit"}}/>
      </div>
      {/* Remove */}
      <button onClick={()=>onRemove(index)} style={{background:"none",border:"none",color:C.light,cursor:"pointer",fontSize:18,lineHeight:1,padding:"0 4px"}}>×</button>
    </div>
  );
});

// ── Plan result card with OOP scenarios ──────────────────────────────────
function PlanResultCard({planResult, rank, memberCount, isSelected, onSelect, pcpVisits, specVisits}) {
  const {plan, facilities, scenarios} = planResult;
  const COLS=[C.blue,C.green,C.amber,"#7C3AED","#db2777"];
  const col=COLS[rank%5], isBest=rank===0;
  const {low,likely,high} = scenarios;

  // Range bar percentage for likely vs high
  const maxOOP = (plan.oopMax||0)*memberCount + (plan.monthlyPremium||0)*12*memberCount;
  const likelyPct = maxOOP>0 ? Math.min((likely.totalOOP/maxOOP)*100, 100) : 50;
  const highPct   = maxOOP>0 ? Math.min((high.totalOOP/maxOOP)*100, 100) : 90;

  return(
    <div onClick={onSelect} style={{background:C.white,borderRadius:12,border:`2px solid ${isSelected?C.blue:isBest?C.blueBdr:C.border}`,marginBottom:14,overflow:"hidden",boxShadow:isBest?"0 2px 12px rgba(37,99,235,0.10)":"none",cursor:"pointer"}}>

      {/* Header */}
      <div style={{background:isBest?C.blueDim:C.bg,borderBottom:`1px solid ${C.border}`,padding:"12px 16px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <div style={{width:10,height:10,borderRadius:"50%",background:col}}/>
          <div>
            <div style={{fontWeight:700,fontSize:15,color:C.text}}>{plan.name||plan.type}</div>
            <div style={{fontSize:11,color:C.muted}}>{plan.type} · ${plan.monthlyPremium}/mo premium · ${(plan.deductible||0).toLocaleString()} deductible · {plan.coinsurance||20}% coinsurance</div>
          </div>
          {isBest&&<span style={{fontSize:10,background:C.green,color:C.white,padding:"2px 8px",borderRadius:10,fontWeight:700,marginLeft:4}}>LOWEST OOP</span>}
        </div>
      </div>

      {/* OOP scenarios — the main value */}
      <div style={{padding:"14px 16px",borderBottom:`1px solid ${C.border}`}}>
        <div style={{fontSize:10,color:C.muted,fontWeight:600,marginBottom:10,letterSpacing:0.3}}>
          YOUR ESTIMATED OUT-OF-POCKET COST / YEAR · {memberCount} MEMBER{memberCount!==1?"S":""}
        </div>

        {/* 3 scenario columns */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:12}}>
          {[
            {label:"Low use",sublabel:`${Math.max(Math.floor((pcpVisits+specVisits)*0.4),1)} visits/yr`,val:low.totalOOP,c:C.green,bg:C.greenDim,bdr:C.greenBdr},
            {label:"Likely",sublabel:`${pcpVisits+specVisits} visits/yr (your estimate)`,val:likely.totalOOP,c:C.blue,bg:C.blueDim,bdr:C.blueBdr},
            {label:"High use",sublabel:`${Math.min((pcpVisits+specVisits)*2,52)} visits/yr`,val:high.totalOOP,c:C.amber,bg:C.amberDim,bdr:C.amberBdr},
          ].map(({label,sublabel,val,c,bg,bdr})=>(
            <div key={label} style={{background:bg,border:`1px solid ${bdr}`,borderRadius:8,padding:"10px 12px",textAlign:"center"}}>
              <div style={{fontSize:10,color:C.muted,marginBottom:2,fontWeight:600}}>{label}</div>
              <div style={{fontSize:20,fontWeight:800,color:c}}>{fmt(val)}</div>
              <div style={{fontSize:9,color:C.light,marginTop:2}}>{sublabel}</div>
            </div>
          ))}
        </div>

        {/* Range bar */}
        <div style={{position:"relative",marginBottom:6}}>
          <div style={{height:6,background:C.bg,border:`1px solid ${C.border}`,borderRadius:6,overflow:"hidden"}}>
            <div style={{position:"absolute",left:0,top:0,height:"100%",width:`${highPct}%`,background:`linear-gradient(90deg,${C.green},${C.amber})`,borderRadius:6}}/>
            <div style={{position:"absolute",left:0,top:0,height:"100%",width:`${likelyPct}%`,background:C.blue,borderRadius:6,opacity:0.6}}/>
          </div>
          <div style={{display:"flex",justifyContent:"space-between",marginTop:3,fontSize:9,color:C.light}}>
            <span>{fmt(low.totalOOP)} (low)</span>
            <span>{fmt(high.totalOOP)} (high)</span>
          </div>
        </div>

        {/* OOP breakdown for likely scenario */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:6,marginTop:10}}>
          {[
            {label:"Annual premium",val:fmt(likely.annualPremium),tip:"You pay this regardless"},
            {label:"Medical OOP",val:fmt(likely.medOOP),tip:"After deductible + coinsurance"},
            {label:likely.hsaContrib>0?"HSA credit":"Avg MRF rate",val:likely.hsaContrib>0?`-${fmt(likely.hsaContrib)}`:fmt(likely.avgRate)+"/visit",tip:likely.hsaContrib>0?"Employer contribution":"Negotiated rate per visit"},
          ].map(({label,val,tip})=>(
            <div key={label} style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:6,padding:"7px 8px"}}>
              <div style={{fontSize:9,color:C.light,marginBottom:1}}>{label}</div>
              <div style={{fontSize:12,fontWeight:700,color:C.text}}>{val}</div>
              <div style={{fontSize:8,color:C.light,marginTop:1}}>{tip}</div>
            </div>
          ))}
        </div>

        {/* Worst case OOP max */}
        <div style={{marginTop:8,fontSize:11,color:C.muted,display:"flex",alignItems:"center",gap:6}}>
          <span style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:4,padding:"2px 7px",fontSize:10,fontWeight:600,color:C.red}}>MAX</span>
          Worst case out-of-pocket: <strong style={{color:C.text}}>{fmt((plan.oopMax||0)*memberCount + (plan.monthlyPremium||0)*12*memberCount - (plan.type==="HSA"?(plan.hsaContribution||0)*memberCount:0))}/yr</strong> (OOP max + premiums)
        </div>
      </div>

      {/* Top 5 facilities */}
      <div style={{padding:"10px 16px"}}>
        <div style={{fontSize:10,color:C.muted,fontWeight:600,marginBottom:8,letterSpacing:0.3}}>TOP {facilities.length} FACILITIES FOR YOUR CARE · MRF NEGOTIATED RATES</div>
        {facilities.length===0
          ?<div style={{fontSize:12,color:C.light,textAlign:"center",padding:"8px 0"}}>No MRF data — try expanding radius</div>
          :facilities.map((f,i)=>(
            <div key={f.npi||i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"6px 0",borderBottom:i<facilities.length-1?`1px solid ${C.border}`:"none"}}>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:12,fontWeight:600,color:C.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                  <span style={{color:C.light,marginRight:5}}>#{i+1}</span>{f.facility}
                </div>
                <div style={{fontSize:11,color:C.muted,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>📍 {f.address}</div>
              </div>
              <div style={{textAlign:"right",marginLeft:12,flexShrink:0}}>
                <div style={{fontSize:14,fontWeight:700,color:i===0?C.green:C.text}}>{fmt(f.negotiated_rate)}</div>
                <div style={{fontSize:9,color:C.light}}>per visit</div>
              </div>
            </div>
          ))
        }
      </div>

      {/* Select */}
      <div style={{padding:"9px 16px",borderTop:`1px solid ${C.border}`,background:isSelected?C.blueDim:C.bg,display:"flex",alignItems:"center",gap:8}}>
        <div style={{width:14,height:14,borderRadius:"50%",border:`2px solid ${isSelected?C.blue:C.border}`,background:isSelected?C.blue:"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
          {isSelected&&<div style={{width:6,height:6,borderRadius:"50%",background:C.white}}/>}
        </div>
        <span style={{fontSize:12,color:isSelected?C.blueD:C.muted,fontWeight:isSelected?600:400}}>
          {isSelected?"✓ Selected — get AI recommendation for this plan":"Click to select this plan"}
        </span>
      </div>
    </div>
  );
}

// ── Section wrapper ───────────────────────────────────────────────────────
function Section({num,title,right,sub,children}){
  return(
    <div style={{background:C.white,borderRadius:12,border:`1.5px solid ${C.border}`,padding:"16px 18px",marginBottom:12}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:sub?6:12}}>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          <span style={{background:C.blue,color:C.white,borderRadius:"50%",width:20,height:20,display:"inline-flex",alignItems:"center",justifyContent:"center",fontSize:10,flexShrink:0}}>{num}</span>
          <span style={{fontSize:13,fontWeight:700,color:C.text}}>{title}</span>
        </div>
        {right}
      </div>
      {sub&&<div style={{fontSize:12,color:C.muted,marginBottom:12}}>{sub}</div>}
      {children}
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────
export default function EnrollmentAdvisor() {
  const [step, setStep] = useState(0);
  const mobile = useIsMobile();

  // Location
  const [zip, setZip]       = useState("");
  const [zipLoading, setZipLoading] = useState(false);
  const [zipSource,  setZipSource]  = useState(""); // "gps" | "manual" | "error"
  const [locationLabelEA, setLocationLabelEA] = useState("");
  const [cityListEA,  setCityListEA]  = useState([]);
  const [citySugsEA,  setCitySugsEA]  = useState([]);
  const [showCitySugEA, setShowCitySugEA] = useState(false);
  const [geoCoords,   setGeoCoords]   = useState(null); // {lat, lng} from city geocode

  // Load city list + geocode helpers
  useEffect(() => {
    const API = process.env.REACT_APP_API_URL || "https://api.mioprice.com";
    fetch(API + "/api/cities").then(r=>r.json()).then(d=>{if(d.cities)setCityListEA(d.cities);}).catch(()=>{});
  }, []);

  const geocodeCityEA = async (input) => {
    try {
      const q = encodeURIComponent(input + (input.includes(',') ? '' : ', USA'));
      const r = await fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=5&q=${q}&countrycodes=us&addressdetails=1`, {headers:{'User-Agent':'MioPrice/1.0'}});
      if(r.ok){const d=await r.json();if(d.length>0){const best=d.find(x=>['city','town'].includes(x.type))||d[0];const city=best.address?.city||best.address?.town||(best.display_name||'').split(',')[0];const state=best.address?.state_code||'';return{lat:parseFloat(best.lat),lng:parseFloat(best.lon),label:(city||'').trim()+(state?', '+state:'')};}}
    }catch{}
    return null;
  };

  // Auto-detect ZIP from browser geolocation on mount
  useEffect(() => {
    if (!navigator.geolocation) return;
    setZipLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { latitude, longitude } = pos.coords;
          // Reverse geocode using free nominatim API
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`,
            { headers: { "Accept-Language": "en-US" } }
          );
          const data = await res.json();
          const detectedZip = data?.address?.postcode?.slice(0, 5);
          if (detectedZip && /^\d{5}$/.test(detectedZip)) {
            setZip(detectedZip);
            setZipSource("gps");
          } else {
            setZipSource("error");
          }
        } catch {
          setZipSource("error");
        }
        setZipLoading(false);
      },
      () => { setZipLoading(false); setZipSource("error"); },
      { timeout: 8000, maximumAge: 300000 }
    );
  }, []);
  const [payer, setPayer]   = useState("");
  const [radius, setRadius] = useState(25);

  // Family members — each: {type:"adult"|"child", gender, age}
  const [members, setMembers] = useState([
    {type:"adult",gender:"",age:""},
  ]);

  // Plans — stored as array of objects; PlanRow uses uncontrolled inputs
  const [plans, setPlans] = useState([
    {type:"PPO",name:"",monthlyPremium:"",deductible:"",oopMax:"",coinsurance:20,copay:30},
    {type:"HMO",name:"",monthlyPremium:"",deductible:"",oopMax:"",coinsurance:20,copay:20},
  ]);
  const [uploadMsg, setUploadMsg] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  // Health
  const [conditions, setConditions] = useState([]);
  const [pcpVisits,  setPcpVisits]  = useState(2);
  const [specVisits, setSpecVisits] = useState(0);

  // Results
  const [loading,      setLoading]      = useState(false);
  const [loadStatus,   setLoadStatus]   = useState("");
  const [planResults,  setPlanResults]  = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);

  // Recommendation
  const [result,       setResult]       = useState(null);
  const [calculating,  setCalculating]  = useState(false);

  const memberCount  = members.length;
  const totalVisits  = Math.max(pcpVisits + specVisits + (conditions.includes("mentalhealth")?12:0), 1);
  const plansReady   = plans.some(p => p.monthlyPremium && p.deductible && p.oopMax);
  const step0Ready   = zip.trim().length>=3 && payer && plansReady;

  // ── Plan handlers (blur-based, avoids re-render lag) ─────────────────
  const handlePlanBlur = useCallback((index, updated) => {
    setPlans(prev => prev.map((p,i) => i===index ? {...p,...updated} : p));
  }, []);

  const removePlan = useCallback((index) => {
    setPlans(prev => prev.filter((_,i) => i!==index));
  }, []);

  const addPlan = useCallback(() => {
    setPlans(prev => [...prev, {type:"PPO",name:"",monthlyPremium:"",deductible:"",oopMax:"",coinsurance:20,copay:30}]);
  }, []);

  // ── Member handlers ───────────────────────────────────────────────────
  const addAdult = () => setMembers(m => [...m, {type:"adult",gender:"",age:""}]);
  const addChild = () => setMembers(m => [...m, {type:"child",gender:"",age:""}]);
  const updateMember = useCallback((i, upd) => setMembers(m => m.map((x,j) => j===i?upd:x)), []);
  const removeMember = useCallback((i) => setMembers(m => m.filter((_,j) => j!==i)), []);

  // ── Upload ────────────────────────────────────────────────────────────
  const handleUpload = useCallback(async (file) => {
    if (!file) return;
    setUploading(true); setUploadMsg("Reading document…");
    try {
      const b64 = await fileToBase64(file);
      const isPDF=file.type==="application/pdf", isImg=file.type.startsWith("image/");
      const messages = isPDF||isImg
        ?[{role:"user",content:[{type:isPDF?"document":"image",source:{type:"base64",media_type:file.type,data:b64}},{type:"text",text:"Extract ALL insurance plan options. Return ONLY valid JSON array, no markdown."}]}]
        :[{role:"user",content:`Extract all plans:\n${(await file.text()).slice(0,4000)}\nReturn ONLY JSON array.`}];
      setUploadMsg("AI extracting plan details…");
      const raw = await callClaude(messages,"Extract ALL insurance plans. Return ONLY JSON array: type, name, monthlyPremium, deductible, oopMax, coinsurance, copay, hsaContribution. No markdown.");
      const extracted = JSON.parse(raw.replace(/```json|```/g,"").trim());
      if (Array.isArray(extracted)&&extracted.length) {
        setPlans(extracted);
        setUploadMsg(`✓ Found ${extracted.length} plans. Review below.`);
      } else setUploadMsg("Couldn't extract — enter manually.");
    } catch { setUploadMsg("Extraction failed — enter manually."); }
    setUploading(false);
  }, []);

  const toggleCond = useCallback((k) => {
    setConditions(prev => {
      // "No chronic conditions" is mutually exclusive with everything else
      if (k === "none") return prev.includes("none") ? [] : ["none"];
      // For any real condition: remove "none", toggle the condition
      const withoutNone = prev.filter(x => x !== "none");
      return withoutNone.includes(k)
        ? withoutNone.filter(x => x !== k)   // deselect
        : [...withoutNone, k];                // select
    });
  }, []);

  // ── Fetch MRF + build plan results ───────────────────────────────────
  const fetchAllPlanResults = useCallback(async () => {
    setLoading(true); setPlanResults([]); setSelectedPlan(null);
    const procs = conditions.length
      ?[...new Set(conditions.flatMap(c=>CONDITION_PROCEDURES[c]||[]))]
      :["annual physical exam"];
    const allRates=[];
    for (const proc of procs.slice(0,3)) {
      setLoadStatus(`Searching MRF rates for "${proc}"…`);
      try { allRates.push(...(await fetchRates(proc,zip,radius,geoCoords?.lat,geoCoords?.lng)).slice(0,20)); }
      catch(e){console.error(e);}
    }
    const byNpi={};
    allRates.forEach(r=>{if(!byNpi[r.npi]||r.negotiated_rate<byNpi[r.npi].negotiated_rate)byNpi[r.npi]=r;});
    const sorted=Object.values(byNpi).sort((a,b)=>a.negotiated_rate-b.negotiated_rate);
    const payerFil=sorted.filter(f=>f.payer?.toLowerCase().includes(payer.toLowerCase())||f.rates?.some(r=>r.payer?.toLowerCase().includes(payer.toLowerCase())));
    const top5=(payerFil.length>0?payerFil:sorted).slice(0,5);
    const avgRate=top5.length>0?top5.reduce((s,f)=>s+f.negotiated_rate,0)/top5.length:150;

    const results = plans
      .filter(p=>p.monthlyPremium&&p.deductible&&p.oopMax)
      .map(plan=>({
        plan,
        facilities: top5,
        scenarios:  calcScenarios(plan, avgRate, pcpVisits, specVisits, memberCount),
      }))
      .sort((a,b)=>a.scenarios.likely.totalOOP - b.scenarios.likely.totalOOP);

    setPlanResults(results);
    setLoadStatus(top5.length>0
      ?`Found ${top5.length} facilities · Comparing ${results.length} plans near ${zip}`
      :"No MRF data found. Try expanding radius.");
    setLoading(false);
    if(results.length>0){setStep(1);setSelectedPlan(0);}
  },[zip,radius,payer,conditions,plans,memberCount,totalVisits]);

  // ── AI recommendation ─────────────────────────────────────────────────
  const generateRec = useCallback(async () => {
    if(selectedPlan===null)return;
    setCalculating(true);
    const top3=planResults[0]?.facilities.slice(0,3).map(f=>`${f.facility} — ${fmt(f.negotiated_rate)}/visit`).join("\n")||"";
    const memberSummary=members.map((m,i)=>`Member ${i+1}: ${m.type}${m.gender?` (${m.gender})`:""}, age ${m.age||"unknown"}`).join("; ");
    const prompt=`${memberSummary}. ZIP:${zip}. Payer:${payer}. Conditions:${conditions.map(c=>CONDITION_LABELS[c]).join(",")||"none"}. Visits:${pcpVisits} PCP+${specVisits} specialist/yr.\n\nPlans ranked by OUT-OF-POCKET cost (likely scenario):\n${planResults.map((r,i)=>`${i+1}. ${r.plan.name||r.plan.type}: Low ${fmt(r.scenarios.low.totalOOP)} / Likely ${fmt(r.scenarios.likely.totalOOP)} / High ${fmt(r.scenarios.high.totalOOP)} out-of-pocket/yr`).join("\n")}\n\nTop facilities:\n${top3}\n\nSelected plan: ${planResults[selectedPlan]?.plan.name||planResults[selectedPlan]?.plan.type}\n\n3 sentences: which plan minimizes out-of-pocket costs and why, which facility to use, one thing to verify with HR. Emphasize that these are OUT-OF-POCKET costs after insurance. Never give medical advice.`;
    const explanation=await callClaude([{role:"user",content:prompt}],"You are a health insurance enrollment advisor using real MRF rate data.");
    setResult({explanation});
    setCalculating(false);
    setStep(2);
  },[planResults,selectedPlan,members,zip,payer,conditions,pcpVisits,specVisits]);

  const adults=members.filter(m=>m.type==="adult");
  const children=members.filter(m=>m.type==="child");

  return(
    <div style={{background:C.page,minHeight:"100vh",fontFamily:"-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",color:C.text}}>

      {/* Banner */}
      <div style={{background:C.white,borderBottom:`1px solid ${C.border}`,padding:mobile?"14px 16px":"16px 32px"}}>
        <div style={{maxWidth:800,margin:"0 auto",display:"flex",alignItems:"center",gap:10}}>
          <span style={{fontSize:20}}>📋</span>
          <div>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <span style={{fontSize:18,fontWeight:800}}>Enrollment Advisor</span>
              <span style={{fontSize:10,background:C.blueDim,color:C.blue,padding:"2px 8px",borderRadius:10,fontWeight:600}}>BETA</span>
            </div>
            <div style={{fontSize:12,color:C.muted,marginTop:1}}>Enter all employer plans · See real MRF rates for top 5 local facilities · Pick the best plan</div>
          </div>
        </div>
      </div>

      <StepBar current={step}/>

      <div style={{maxWidth:800,margin:"0 auto",padding:"24px 20px"}}>

        {/* ══ STEP 0 ════════════════════════════════════════════════════════ */}
        {step===0&&(
          <div>
            {/* ① Location & Payer */}
            <Section num="1" title="Your location & insurance">
              <div style={{display:"grid",gridTemplateColumns:mobile?"1fr 1fr":"180px 1fr 120px",gap:12}}>
                <div>
                  <div style={{fontSize:11,color:C.muted,marginBottom:4,fontWeight:500,display:"flex",alignItems:"center",gap:6}}>
                    ZIP code
                    {zipLoading&&<span style={{fontSize:10,color:C.amber}}>📍 Detecting…</span>}
                    {zipSource==="gps"&&!zipLoading&&<span style={{fontSize:10,color:C.green,fontWeight:600}}>📍 Auto-detected</span>}
                  </div>
                  <div style={{position:"relative"}}>
                    <div style={{position:"relative"}}>
                    <input value={zip} onBlur={()=>setTimeout(()=>setShowCitySugEA(false),150)} onChange={e=>{const v=e.target.value;setZip(v);setZipSource("manual");setLocationLabelEA("");if(v.length>=2&&!/^\d{5}$/.test(v.trim())){const m=cityListEA.filter(c=>c.toLowerCase().startsWith(v.toLowerCase())).slice(0,6);setCitySugsEA(m);setShowCitySugEA(m.length>0);}else{setCitySugsEA([]);setShowCitySugEA(false);}}} placeholder={zipLoading?"Detecting…":"ZIP or city"}
                      style={{width:"100%",background:zipLoading?C.bg:C.bg,border:`1.5px solid ${zip.length>=3?C.blue:zipLoading?C.amberBdr:C.border}`,borderRadius:8,color:C.text,padding:"9px 11px",fontSize:16,fontWeight:700,outline:"none",boxSizing:"border-box",fontFamily:"inherit"}}/>
                    {showCitySugEA&&citySugsEA.length>0&&(
                      <div style={{position:"absolute",top:"100%",left:0,right:0,background:"#fff",border:"1px solid #e5e7eb",borderRadius:8,boxShadow:"0 4px 12px rgba(0,0,0,0.1)",zIndex:100,overflow:"hidden"}}>
                        {citySugsEA.map((city,i)=>(
                          <div key={city} onMouseDown={()=>{setZip(city);setLocationLabelEA(city);setCitySugsEA([]);setShowCitySugEA(false);}}
                            style={{padding:"9px 14px",cursor:"pointer",fontSize:13,borderBottom:i<citySugsEA.length-1?"1px solid #f3f4f6":"none"}}>📍 {city}</div>
                        ))}
                      </div>
                    )}
                    </div>
                    {/* GPS re-detect button */}
                    {!zipLoading&&zipSource!=="gps"&&(
                      <button
                        onClick={()=>{
                          setZipLoading(true); setZipSource("");
                          navigator.geolocation?.getCurrentPosition(
                            async(pos)=>{
                              try{
                                const res=await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${pos.coords.latitude}&lon=${pos.coords.longitude}&format=json`,{headers:{"Accept-Language":"en-US"}});
                                const data=await res.json();
                                const z=data?.address?.postcode?.slice(0,5);
                                if(z&&/^\d{5}$/.test(z)){setZip(z);setZipSource("gps");}
                              }catch{}
                              setZipLoading(false);
                            },
                            ()=>setZipLoading(false),
                            {timeout:8000}
                          );
                        }}
                        title="Detect my location"
                        style={{position:"absolute",right:8,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",cursor:"pointer",fontSize:16,lineHeight:1,color:C.light,padding:2}}>
                        📍
                      </button>
                    )}
                  </div>
                </div>
                <div style={mobile?{gridColumn:"1 / -1",gridRow:2}:undefined}>
                  <div style={{fontSize:11,color:C.muted,marginBottom:4,fontWeight:500}}>Insurance payer</div>
                  <select value={payer} onChange={e=>setPayer(e.target.value)}
                    style={{width:"100%",background:C.bg,border:`1.5px solid ${payer?C.blue:C.border}`,borderRadius:8,color:payer?C.text:C.light,padding:"9px 11px",fontSize:14,outline:"none",fontFamily:"inherit",cursor:"pointer"}}>
                    <option value="">Select your payer…</option>
                    {PAYERS.map(p=><option key={p.value} value={p.value}>{p.label}</option>)}
                  </select>
                </div>
                <div style={mobile?{gridColumn:2,gridRow:1}:undefined}>
                  <div style={{fontSize:11,color:C.muted,marginBottom:4,fontWeight:500}}>Radius: <span style={{color:C.blue,fontWeight:700}}>{radius}mi</span></div>
                  <input type="range" min={5} max={50} step={5} value={radius} onChange={e=>setRadius(parseInt(e.target.value))} style={{width:"100%",marginTop:10,accentColor:C.blue}}/>
                </div>
              </div>
            </Section>

            {/* ② Family members */}
            <Section num="2" title="Family members"
              sub="Add each person who will be on this plan — we use age and gender to model healthcare needs."
              right={null}>

              {/* Add buttons + counts — together so user doesn't have to reach */}
              <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:12,flexWrap:"wrap"}}>
                <button onClick={addAdult} style={{fontSize:13,color:C.blue,fontWeight:600,border:`1.5px solid ${C.blueBdr}`,background:C.blueDim,borderRadius:8,padding:"7px 14px",cursor:"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",gap:5}}>
                  <span style={{fontSize:15}}>+</span> Adult
                </button>
                <button onClick={addChild} style={{fontSize:13,color:C.amber,fontWeight:600,border:`1.5px solid ${C.amberBdr}`,background:C.amberDim,borderRadius:8,padding:"7px 14px",cursor:"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",gap:5}}>
                  <span style={{fontSize:15}}>+</span> Child
                </button>
                {/* Count badges inline with buttons */}
                <div style={{width:1,height:24,background:C.border,margin:"0 4px"}}/>
                <div style={{background:C.blueDim,border:`1px solid ${C.blueBdr}`,borderRadius:8,padding:"6px 12px",fontSize:12,color:C.blueD,fontWeight:600}}>
                  👤 {adults.length} Adult{adults.length!==1?"s":""}
                </div>
                <div style={{background:C.amberDim,border:`1px solid ${C.amberBdr}`,borderRadius:8,padding:"6px 12px",fontSize:12,color:C.amber,fontWeight:600}}>
                  🧒 {children.length} Child{children.length!==1?"ren":""}
                </div>
                <div style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:8,padding:"6px 12px",fontSize:12,color:C.muted}}>
                  Total: {memberCount}
                </div>
              </div>

              {/* Member rows */}
              {members.map((m,i)=>(
                <MemberRow key={i} member={m} index={i} onChange={updateMember} onRemove={removeMember}/>
              ))}
              {members.length===0&&(
                <div style={{textAlign:"center",padding:"16px 0",color:C.light,fontSize:13}}>
                  No members added. Click + Adult or + Child above.
                </div>
              )}
            </Section>

            {/* ③ Plans */}
            <Section num="3" title="All employer plan options"
              sub="Enter every plan your employer offers. We'll calculate the real annual cost for each."
              right={null}>

              {/* ── Upload options: camera / file / manual ── */}
              {!uploadMsg.includes("✓")&&(
                <div style={{marginBottom:14}}>
                  <div style={{fontSize:12,color:C.muted,marginBottom:8}}>
                    Fastest: upload or photograph your benefits summary — AI extracts all plans automatically.
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:8}}>
                    {/* 📷 Take photo — triggers camera on mobile */}
                    <label style={{display:"flex",flexDirection:"column",alignItems:"center",gap:5,cursor:"pointer",border:`1.5px solid ${C.blueBdr}`,background:C.blueDim,borderRadius:10,padding:"14px 8px",textAlign:"center"}}>
                      <span style={{fontSize:24}}>📷</span>
                      <span style={{fontSize:12,fontWeight:600,color:C.blue}}>Take photo</span>
                      <span style={{fontSize:10,color:C.muted}}>Camera on mobile</span>
                      <input type="file" accept="image/*" capture="environment" style={{display:"none"}}
                        onChange={e=>handleUpload(e.target.files[0])}/>
                    </label>
                    {/* 📄 Upload file */}
                    <label style={{display:"flex",flexDirection:"column",alignItems:"center",gap:5,cursor:"pointer",border:`1.5px solid ${C.border}`,background:C.bg,borderRadius:10,padding:"14px 8px",textAlign:"center"}}>
                      <span style={{fontSize:24}}>📄</span>
                      <span style={{fontSize:12,fontWeight:600,color:C.text2}}>{uploading?"Reading…":"Upload file"}</span>
                      <span style={{fontSize:10,color:C.muted}}>PDF, Excel, CSV, image</span>
                      <input ref={fileRef} type="file" accept=".pdf,.xlsx,.xls,.csv,.png,.jpg,.jpeg" style={{display:"none"}}
                        onChange={e=>handleUpload(e.target.files[0])}/>
                    </label>
                    {/* ✏️ Enter manually */}
                    <button onClick={()=>setUploadMsg("manual")}
                      style={{display:"flex",flexDirection:"column",alignItems:"center",gap:5,cursor:"pointer",border:`1.5px solid ${C.border}`,background:C.bg,borderRadius:10,padding:"14px 8px",fontFamily:"inherit"}}>
                      <span style={{fontSize:24}}>✏️</span>
                      <span style={{fontSize:12,fontWeight:600,color:C.text2}}>Enter manually</span>
                      <span style={{fontSize:10,color:C.muted}}>Type plan details</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Upload status */}
              {uploadMsg&&uploadMsg!=="manual"&&(
                <div style={{background:uploadMsg.includes("✓")?C.greenDim:C.amberDim,border:`1px solid ${uploadMsg.includes("✓")?C.greenBdr:C.amberBdr}`,borderRadius:7,padding:"8px 12px",marginBottom:10,fontSize:12,color:uploadMsg.includes("✓")?C.green:C.amber,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <span>{uploadMsg}</span>
                  <button onClick={()=>setUploadMsg("")} style={{background:"none",border:"none",color:C.light,cursor:"pointer",fontSize:16,lineHeight:1}}>×</button>
                </div>
              )}

              {/* Show plan rows once upload done or manual chosen */}
              {(uploadMsg.includes("✓")||uploadMsg==="manual")&&(
                <>
                  {plans.map((p,i)=>(
                    <PlanRow key={`plan-${i}`} plan={p} index={i} onBlurUpdate={handlePlanBlur} onRemove={removePlan}/>
                  ))}
                  {plans.length<6&&(
                    <button onClick={addPlan} style={{width:"100%",background:"none",border:`1.5px dashed ${C.border}`,borderRadius:8,padding:9,color:C.muted,cursor:"pointer",fontSize:12,marginTop:4,fontFamily:"inherit"}}>
                      + Add another plan option
                    </button>
                  )}
                  <button onClick={()=>{setUploadMsg("");}} style={{marginTop:8,background:"none",border:"none",color:C.light,cursor:"pointer",fontSize:11,fontFamily:"inherit",textDecoration:"underline"}}>
                    ← Back to upload options
                  </button>
                </>
              )}
            </Section>

            {/* ④ Health & Usage */}
            <Section num="4" title="Health conditions & expected usage"
              sub="Select all that apply — you can choose multiple conditions.">
              {/* Selected count badge */}
              {conditions.length>0&&!conditions.includes("none")&&(
                <div style={{display:"inline-flex",alignItems:"center",gap:6,background:C.blueDim,border:`1px solid ${C.blueBdr}`,borderRadius:8,padding:"5px 12px",marginBottom:10,fontSize:12,color:C.blueD,fontWeight:600}}>
                  ✓ {conditions.length} condition{conditions.length!==1?"s":""} selected: {conditions.map(c=>CONDITION_LABELS[c]).join(", ")}
                </div>
              )}
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:6,marginBottom:12}}>
                {Object.entries(CONDITION_LABELS).map(([k,label])=>{
                  const sel=conditions.includes(k);
                  const isNone=k==="none";
                  return(
                    <button key={k} onClick={()=>toggleCond(k)}
                      style={{padding:"8px 10px",borderRadius:8,cursor:"pointer",textAlign:"left",fontSize:12,fontFamily:"inherit",lineHeight:1.3,
                        background:sel?(isNone?C.bg:C.blueDim):C.white,
                        border:`1.5px solid ${sel?(isNone?C.border:C.blue):C.border}`,
                        color:sel?(isNone?C.muted:C.blueD):C.text2,
                        fontWeight:sel?600:400,
                        opacity:(!sel&&conditions.includes("none")&&!isNone)?0.4:1,
                      }}>
                      {sel?"✓ ":""}{label}
                    </button>
                  );
                })}
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                {[{label:"Expected PCP visits/year",val:pcpVisits,set:setPcpVisits},{label:"Expected specialist visits/year",val:specVisits,set:setSpecVisits}].map(({label,val,set})=>(
                  <div key={label}>
                    <div style={{fontSize:12,color:C.muted,marginBottom:4,fontWeight:500}}>{label}</div>
                    <input type="number" min="0" value={val} onChange={e=>set(parseInt(e.target.value)||0)}
                      style={{width:"100%",background:C.white,border:`1px solid ${C.border}`,borderRadius:8,color:C.text,padding:"9px 11px",fontSize:13,outline:"none",boxSizing:"border-box",fontFamily:"inherit"}}/>
                  </div>
                ))}
              </div>
            </Section>

            {/* Ready + CTA */}
            {step0Ready&&(
              <div style={{background:C.greenDim,border:`1px solid ${C.greenBdr}`,borderRadius:8,padding:"10px 14px",marginBottom:12,fontSize:13,color:C.green}}>
                ✓ Ready — {payer} MRF rates within {radius}mi of {zip} · {memberCount} member{memberCount!==1?"s":""} · {plans.filter(p=>p.monthlyPremium&&p.deductible&&p.oopMax).length} plan{plans.filter(p=>p.monthlyPremium&&p.deductible&&p.oopMax).length!==1?"s":""}
              </div>
            )}
            {loadStatus&&(
              <div style={{background:loading?C.amberDim:C.greenDim,border:`1px solid ${loading?C.amberBdr:C.greenBdr}`,borderRadius:8,padding:"9px 13px",marginBottom:12,fontSize:13,color:loading?C.amber:C.green}}>
                {loading?"⏳ ":"✓ "}{loadStatus}
              </div>
            )}
            <button onClick={fetchAllPlanResults} disabled={!step0Ready||loading}
              style={{width:"100%",background:step0Ready&&!loading?C.blue:C.blueBdr,color:C.white,border:"none",borderRadius:8,padding:14,fontWeight:700,fontSize:15,cursor:step0Ready&&!loading?"pointer":"not-allowed",fontFamily:"inherit"}}>
              {loading?"Fetching MRF rates…":`🔍 Compare ${plans.filter(p=>p.monthlyPremium&&p.deductible&&p.oopMax).length} plan${plans.filter(p=>p.monthlyPremium&&p.deductible&&p.oopMax).length!==1?"s":""} — find best value near ${zip||"your ZIP"} →`}
            </button>
          </div>
        )}

        {/* ══ STEP 1: Plan comparison ════════════════════════════════════════ */}
        {step===1&&planResults.length>0&&(
          <div>
            <h3 style={{fontSize:16,fontWeight:700,margin:"0 0 4px"}}>Your employer's plans compared</h3>
            <p style={{color:C.muted,fontSize:13,margin:"0 0 14px"}}>{loadStatus}</p>

            {/* Summary bar */}
            <div style={{background:C.white,borderRadius:10,border:`1px solid ${C.border}`,padding:"12px 16px",marginBottom:16,display:"flex",gap:16,alignItems:"center",flexWrap:"wrap"}}>
              <div>
                <div style={{fontSize:10,color:C.light,marginBottom:1}}>Lowest OOP (likely)</div>
                <div style={{fontSize:14,fontWeight:700,color:C.green}}>
                  {planResults[0]?.plan.name||planResults[0]?.plan.type} — {fmt(planResults[0]?.scenarios.likely.totalOOP)}/yr
                </div>
              </div>
              <div style={{width:1,height:32,background:C.border}}/>
              <div>
                <div style={{fontSize:10,color:C.light,marginBottom:1}}>Highest OOP (likely)</div>
                <div style={{fontSize:14,fontWeight:700,color:C.amber}}>
                  {planResults[planResults.length-1]?.plan.name||planResults[planResults.length-1]?.plan.type} — {fmt(planResults[planResults.length-1]?.scenarios.likely.totalOOP)}/yr
                </div>
              </div>
              <div style={{width:1,height:32,background:C.border}}/>
              <div>
                <div style={{fontSize:10,color:C.light,marginBottom:1}}>You could save</div>
                <div style={{fontSize:14,fontWeight:700,color:C.blue}}>
                  {fmt((planResults[planResults.length-1]?.scenarios.likely.totalOOP||0)-(planResults[0]?.scenarios.likely.totalOOP||0))}/yr
                </div>
              </div>
              <div style={{width:1,height:32,background:C.border}}/>
              <div>
                <div style={{fontSize:10,color:C.light,marginBottom:1}}>MRF rates near</div>
                <div style={{fontSize:13,fontWeight:600,color:C.text}}>{payer} · {zip}</div>
              </div>
            </div>

            {planResults.map((pr,i)=>(
              <PlanResultCard key={i} planResult={pr} rank={i} memberCount={memberCount}
                isSelected={selectedPlan===i} onSelect={()=>setSelectedPlan(i)}
                pcpVisits={pcpVisits} specVisits={specVisits}/>
            ))}

            <div style={{padding:"9px 13px",background:C.bg,border:`1px solid ${C.border}`,borderRadius:8,marginBottom:14,fontSize:11,color:C.light}}>
              ⓘ Medical costs estimated using average {payer} MRF rates near {zip}, modeled against {totalVisits} visit{totalVisits!==1?"s":""}/yr across {memberCount} member{memberCount!==1?"s":""}. Actual costs vary.
            </div>

            <div style={{display:"flex",gap:10}}>
              <button onClick={()=>setStep(0)} style={{background:C.white,border:`1.5px solid ${C.border}`,borderRadius:8,padding:"11px 20px",color:C.muted,cursor:"pointer",fontSize:14,fontFamily:"inherit",fontWeight:600}}>← Back</button>
              <button onClick={generateRec} disabled={selectedPlan===null||calculating}
                style={{flex:1,background:(selectedPlan===null||calculating)?C.blueBdr:C.blue,color:C.white,border:"none",borderRadius:8,padding:12,fontWeight:600,fontSize:14,cursor:(selectedPlan===null||calculating)?"not-allowed":"pointer",fontFamily:"inherit"}}>
                {calculating?"Generating…":`Get AI recommendation for ${planResults[selectedPlan]?.plan.name||planResults[selectedPlan]?.plan.type||"selected plan"} →`}
              </button>
            </div>
          </div>
        )}

        {/* ══ STEP 2: Recommendation ════════════════════════════════════════ */}
        {step===2&&result&&(
          <div>
            <h3 style={{fontSize:16,fontWeight:700,margin:"0 0 4px"}}>Your enrollment recommendation</h3>
            <p style={{color:C.muted,fontSize:13,margin:"0 0 16px"}}>
              {adults.length} adult{adults.length!==1?"s":""}{children.length>0?`, ${children.length} child${children.length!==1?"ren":""}`:""}  · {payer} · ZIP {zip} · {conditions.map(c=>CONDITION_LABELS[c]).join(", ")||"general health"}
            </p>

            <div style={{background:C.blueDim,border:`1px solid ${C.blueBdr}`,borderRadius:12,padding:16,marginBottom:18}}>
              <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:10}}>
                <div style={{width:6,height:6,borderRadius:"50%",background:C.blue}}/>
                <span style={{fontSize:11,fontWeight:700,color:C.blueD}}>AI RECOMMENDATION · Real {payer} MRF rates near ZIP {zip}</span>
              </div>
              <p style={{margin:0,fontSize:14,lineHeight:1.65,color:C.text}}>{result.explanation}</p>
            </div>

            {/* All plans summary table */}
            <div style={{background:C.white,borderRadius:12,border:`1.5px solid ${C.border}`,overflow:"hidden",marginBottom:16}}>
              <div style={{padding:"12px 16px",borderBottom:`1px solid ${C.border}`,fontSize:12,fontWeight:700,color:C.muted,letterSpacing:0.3}}>ALL PLANS — ANNUAL COST</div>
              {planResults.map((pr,i)=>{
                const COLS=[C.blue,C.green,C.amber,"#7C3AED","#db2777"];
                const isBest=i===0,isSel=selectedPlan===i;
                const {low,likely,high}=pr.scenarios;
                return(
                  <div key={i} style={{padding:"11px 16px",borderBottom:i<planResults.length-1?`1px solid ${C.border}`:"none",background:isSel?C.blueDim:isBest?C.greenDim:"transparent",display:"flex",alignItems:"center",gap:12}}>
                    <div style={{width:10,height:10,borderRadius:"50%",background:COLS[i%5],flexShrink:0}}/>
                    <div style={{flex:1}}>
                      <div style={{fontWeight:600,fontSize:13}}>{pr.plan.name||pr.plan.type}</div>
                      <div style={{fontSize:11,color:C.muted}}>
                        Low {fmt(low.totalOOP)} · Likely {fmt(likely.totalOOP)} · High {fmt(high.totalOOP)} out-of-pocket/yr
                      </div>
                    </div>
                    <div style={{textAlign:"right"}}>
                      <div style={{fontSize:16,fontWeight:800,color:isBest?C.green:C.text}}>{fmt(likely.totalOOP)}</div>
                      <div style={{fontSize:9,color:C.light}}>likely OOP/yr</div>
                    </div>
                    {isBest&&<span style={{fontSize:9,background:C.green,color:C.white,padding:"2px 7px",borderRadius:8,fontWeight:700,flexShrink:0}}>BEST</span>}
                    {isSel&&!isBest&&<span style={{fontSize:9,background:C.blue,color:C.white,padding:"2px 7px",borderRadius:8,fontWeight:700,flexShrink:0}}>YOUR PICK</span>}
                  </div>
                );
              })}
            </div>

            {/* Top facilities */}
            <div style={{background:C.white,borderRadius:12,border:`1.5px solid ${C.border}`,overflow:"hidden",marginBottom:16}}>
              <div style={{padding:"12px 16px",borderBottom:`1px solid ${C.border}`,fontSize:12,fontWeight:700,color:C.muted,letterSpacing:0.3}}>TOP FACILITIES · {payer} MRF RATES</div>
              {(planResults[0]?.facilities||[]).map((f,i)=>(
                <div key={f.npi||i} style={{padding:"11px 16px",borderBottom:i<(planResults[0]?.facilities||[]).length-1?`1px solid ${C.border}`:"none",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <div><div style={{fontWeight:600,fontSize:13}}>{f.facility}</div><div style={{fontSize:11,color:C.muted}}>📍 {f.address}</div></div>
                  <div style={{textAlign:"right"}}><div style={{fontSize:16,fontWeight:700,color:i===0?C.green:C.text}}>{fmt(f.negotiated_rate)}</div><div style={{fontSize:9,color:C.light}}>per visit</div></div>
                </div>
              ))}
            </div>

            <div style={{padding:"11px 14px",background:C.bg,border:`1px solid ${C.border}`,borderRadius:10,marginBottom:14,fontSize:11,color:C.light,lineHeight:1.7}}>
              <strong style={{color:C.muted}}>ⓘ</strong> Cost projections use real {payer} MRF negotiated rates near ZIP {zip}. Actual costs depend on providers, plan design, and utilization. Verify all plan details with HR before enrolling.
            </div>

            <div style={{display:"flex",gap:10}}>
              <button onClick={()=>{setStep(0);setResult(null);setPlanResults([]);setConditions([]);setUploadMsg("");setZip("");setPayer("");}} style={{flex:1,background:C.white,border:`1.5px solid ${C.border}`,borderRadius:8,padding:12,color:C.muted,cursor:"pointer",fontSize:13,fontFamily:"inherit"}}>Start over</button>
              <button onClick={()=>setStep(1)} style={{flex:1,background:C.white,border:`1.5px solid ${C.border}`,borderRadius:8,padding:12,color:C.text2,cursor:"pointer",fontSize:13,fontFamily:"inherit"}}>← Compare plans</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
